import React from 'react';
import { getDistanceUnits, getEmissionsUnits, getFuelUnits, formatNumber } from "./UtilityFunctions";

//Default column width for table is 100
const MED_COL = 150;
const LARGE_COL = 200;

const numberSort = (rowA, rowB, columnId) => {
    let a = rowA.values[columnId];
    let b = rowB.values[columnId];
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a > b ? 1 : -1;
};

const stringSort = (rowA, rowB, columnId) => {
    const a = rowA.values[columnId]?.toLowerCase();
    const b = rowB.values[columnId]?.toLowerCase();
    return a > b ? 1 : -1;
}

const sharedColumns = (isTd, metricUnitPrefs, shorthand) => {
    let sharedColumns = [
        { Header: "Asset ID", accessor: "asset_id", sortType: stringSort, width: MED_COL },
        { Header: "VIN", accessor: "vin", sortType: stringSort, width: LARGE_COL },
        { Header: "Year", accessor: "year", sortType: "basic" },
        { Header: "Make", accessor: "make", sortType: stringSort, width: MED_COL },
        { Header: "Model", accessor: "model", sortType: stringSort, width: LARGE_COL },
        { Header: "Vehicle Class", accessor: "vehicle_class", sortType: stringSort, width: MED_COL },
        { Header: "Fuel Type", accessor: "drivetrain", sortType: stringSort },
    ]
    if (isTd) {
        const distanceUnits = getDistanceUnits(metricUnitPrefs.distance, shorthand);
        sharedColumns.push(
            { Header: `Distance (${distanceUnits})`, excelHeader: `Distance \n(${distanceUnits})`, accessor: 'distance', sortType: numberSort }
        );
    }
    return sharedColumns;
};

const tdSpecificAllVehiclesColumns = (isTd, userSettings) => {
    if (isTd) {
        return (
            [
                // NOTE: Hiding the emission ratio for now, per slack discussion with SB. May be reintroduced in the future pending further processor improvements, so not removing it entirely. - NK
                // { Header: "Emission Ratio", accessor: "emit", sortType: _decimalStringSort, showToolTip: () => { } },
                { Header: `Fuel Economy \n(${ userSettings.liquid_fuel_economy})`, accessor: 'fuelEcon', sortType: 'alphanumeric' },
                { Header: "Idle Hours", accessor: "idleHours", sortType: numberSort }
            ]
        );
    } else {
        return [];
    }
}

const fxSpecificColumns = (isTd, userSettings, metricUnitPrefs, shorthand) => {

    if (!isTd) {
         const fuelUnits = getFuelUnits(metricUnitPrefs.volume, shorthand);
        return [
            {Header: `Fuel\n(${fuelUnits})`, accessor: userSettings.use_liters ? 'fu_liters' : 'fuel_gallons'}
        ]
    } else {
        return [];
    }
}

export const allVehiclesTableColumns = (isTd, methodology, metricUnitPrefs, macroScale, shorthand, userSettings) => {
    const FINE_PM = formatNumber(2.5, userSettings);

    const ghgUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, shorthand);
    const co2Units = getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, shorthand);
    const coUnits = getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, shorthand);
    const noxUnits = getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, shorthand);
    const pm10Units = getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, shorthand);
    const pm25Units = getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, shorthand);
    const ch4Units = getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, shorthand);
    const bioco2Units = getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, shorthand);

    if (methodology?.value === 'moves3') {
        return (
            [
                ...sharedColumns(isTd, metricUnitPrefs, shorthand),
                ...tdSpecificAllVehiclesColumns(isTd, userSettings),
                ...fxSpecificColumns(isTd, userSettings, metricUnitPrefs, shorthand),
                { Header: `GHG \n(${ghgUnits})`, accessor: 'ghgGenerated', sortType: numberSort },
                { Header: <div>CO<sub>2</sub><br /> ({co2Units})</div>, excelHeader: `CO₂ \n(${co2Units})`, testHeader: `CO2 (${co2Units})`, accessor: 'co2Generated', sortType: numberSort, width: MED_COL },
                { Header: `CO \n(${coUnits})`, accessor: 'coGenerated', sortType: numberSort, width: MED_COL },
                { Header: <div>NO<sub>x</sub><br /> ({noxUnits})</div>, excelHeader: `NOₓ \n(${noxUnits})`, testHeader: `NOx (${noxUnits})`, accessor: 'noxGenerated', sortType: numberSort },
                { Header: `PM 10\u00B5m \n(${pm10Units})`, accessor: 'pm10Generated', sortType: numberSort },
                { Header: `PM ${FINE_PM}\u00B5m \n(${pm25Units})`, accessor: 'pm25Generated', sortType: numberSort }
            ]
        )
    } else {
        return (
            [
                ...sharedColumns(isTd, metricUnitPrefs, shorthand),
                ...tdSpecificAllVehiclesColumns(isTd, userSettings),
                ...fxSpecificColumns(isTd, userSettings, metricUnitPrefs, shorthand),
                { Header: `GHG \n(${ghgUnits})`, accessor: 'ghgGenerated', sortType: numberSort },
                { Header: <div>CO<sub>2</sub><br /> ({co2Units})</div>, excelHeader: `CO₂ \n(${co2Units})`, testHeader: `CO2 (${co2Units})`, accessor: 'co2Generated', sortType: numberSort, width: MED_COL },
                { Header: <div>CH<sub>4</sub><br /> ({ch4Units})</div>, excelHeader: `CH4 \n(${ch4Units})`, testHeader: `CH4 (${ch4Units})`, accessor: 'ch4Generated', sortType: numberSort, width: MED_COL },
                { Header: <div>NO<sub>x</sub><br /> ({noxUnits})</div>, excelHeader: `NOₓ \n(${noxUnits})`, testHeader: `NOx (${noxUnits})`, accessor: 'noxGenerated', sortType: numberSort },
                { Header: <div>Biofuel CO<sub>2</sub><br /> ({bioco2Units})</div>, excelHeader: `Biofuel CO₂ \n(${bioco2Units})`, testHeader: `Biofuel CO2 (${bioco2Units})`, accessor: 'bioco2Generated', sortType: numberSort, width: MED_COL },
            ]
        )
    }
}

export const altFuelVehiclesTableColumns = (isTd, methodology, metricUnitPrefs, macroScale, shorthand, userSettings) => {
    const FINE_PM = formatNumber(2.5, userSettings);
    
    const ghgUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, shorthand);
    const co2Units = getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, shorthand);
    const coUnits = getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, shorthand);
    const noxUnits = getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, shorthand);
    const pm10Units = getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, shorthand);
    const pm25Units = getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, shorthand);
    const ch4Units = getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, shorthand);
    const bioco2Units = getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, shorthand);
    const fuelUnits = getFuelUnits(metricUnitPrefs.volume, shorthand);

    if (methodology?.value === 'moves3') {
        return (
            [
                ...sharedColumns(isTd, metricUnitPrefs, shorthand),
                { Header: `GHG \n(${ghgUnits})`, accessor: 'ghgAvoided', sortType: numberSort },
                { Header: <div>CO<sub>2</sub> Avoided<br /> ({co2Units})</div>, excelHeader: `CO₂ Avoided \n(${co2Units})`, testHeader: `CO2 Avoided (${co2Units})`, accessor: 'co2Avoided', sortType: numberSort, width: MED_COL },
                { Header: `CO Avoided \n(${coUnits})`, accessor: 'coAvoided', sortType: numberSort, width: MED_COL },
                { Header: <div>NO<sub>x</sub> Avoided<br /> ({noxUnits})</div>, excelHeader: `NOₓ Avoided \n(${noxUnits})`, testHeader: `NOx Avoided (${noxUnits})`, accessor: 'noxAvoided', sortType: numberSort},
                { Header: `PM 10\u00B5m Avoided \n(${pm10Units})`, accessor: 'pm10Avoided', sortType: numberSort, width: MED_COL },
                { Header: `PM ${FINE_PM}\u00B5m Avoided \n(${pm25Units})`, accessor: 'pm25Avoided', sortType: numberSort, width: MED_COL },
                { Header: `Fuel Reduction \n(${fuelUnits})`, accessor: "fuelUnitsSaved", sortType: numberSort },
                { Header: "Fuel Savings", accessor: "fuelCostSaved", sortType: numberSort }
            ]
        )
    } else {
        return (
            [
                ...sharedColumns(isTd, metricUnitPrefs, shorthand),
                { Header: `GHG \n(${ghgUnits})`, accessor: 'ghgAvoided', sortType: numberSort },
                { Header: <div>CO<sub>2</sub> Avoided<br /> ({co2Units})</div>, excelHeader: `CO₂ Avoided \n(${co2Units})`, testHeader: `CO2 Avoided (${co2Units})`, accessor: 'co2Avoided', sortType: numberSort, width: MED_COL },
                { Header: <div>CH<sub>4</sub> Avoided<br /> ({ch4Units})</div>, excelHeader: `CH4 Avoided \n(${ch4Units})`, testHeader: `CH4 Avoided (${ch4Units})`, accessor: 'ch4Avoided', sortType: numberSort, width: MED_COL },
                { Header: <div>NO<sub>x</sub> Avoided<br /> ({noxUnits})</div>, excelHeader: `NOₓ Avoided \n(${noxUnits})`, testHeader: `NOx Avoided (${noxUnits})`, accessor: 'noxAvoided', sortType: numberSort },
                { Header: <div>Biofuel CO<sub>2</sub> Avoided<br /> ({bioco2Units})</div>, excelHeader: `Biofuel CO₂ Avoided \n(${bioco2Units})`, testHeader: `Biofuel CO2 Avoided (${bioco2Units})`, accessor: 'bioco2Avoided', sortType: numberSort, width: MED_COL },
                { Header: `Fuel Reduction \n(${fuelUnits})`, accessor: "fuelUnitsSaved", sortType: numberSort },
                { Header: "Fuel Savings", accessor: "fuelCostSaved", sortType: numberSort }
            ]
        )
    }
}